import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Table,
} from "reactstrap";
import { getActiveColorStyle } from "../../utils/colorUtils";

const FigureADeal = ({ activeColor = "blue" }) => {
  const [formData, setFormData] = useState({
    vehiclePrice: "",
    downPayment: "",
    tradeInValue: "",
    taxRate: "8.25",
    termMonths: "60",
    interestRate: "5.5"
  });

  const [calculatedResults, setCalculatedResults] = useState(null);

  // Get active color style
  const activeColorStyle = getActiveColorStyle(activeColor);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateDeal = () => {
    const vehiclePrice = parseFloat(formData.vehiclePrice) || 0;
    const downPayment = parseFloat(formData.downPayment) || 0;
    const tradeInValue = parseFloat(formData.tradeInValue) || 0;
    const taxRate = parseFloat(formData.taxRate) || 0;
    const termMonths = parseInt(formData.termMonths) || 60;
    const interestRate = parseFloat(formData.interestRate) || 0;

    const subtotal = vehiclePrice - tradeInValue;
    const taxAmount = subtotal * (taxRate / 100);
    const totalAmount = subtotal + taxAmount;
    const financedAmount = totalAmount - downPayment;
    const monthlyRate = interestRate / 100 / 12;
    const monthlyPayment = financedAmount * (monthlyRate * Math.pow(1 + monthlyRate, termMonths)) / (Math.pow(1 + monthlyRate, termMonths) - 1);

    setCalculatedResults({
      subtotal: subtotal.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
      financedAmount: financedAmount.toFixed(2),
      monthlyPayment: monthlyPayment.toFixed(2),
      totalInterest: (monthlyPayment * termMonths - financedAmount).toFixed(2),
      totalCost: (monthlyPayment * termMonths + downPayment).toFixed(2)
    });
  };

  return (
    <div className="content">
      <Row>
        <Col md="6">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Deal Calculator</CardTitle>
            </CardHeader>
            <CardBody>
              <Form>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label for="vehiclePrice">Vehicle Price ($)</Label>
                      <Input
                        type="number"
                        name="vehiclePrice"
                        id="vehiclePrice"
                        value={formData.vehiclePrice}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="0.00"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="downPayment">Down Payment ($)</Label>
                      <Input
                        type="number"
                        name="downPayment"
                        id="downPayment"
                        value={formData.downPayment}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="0.00"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label for="tradeInValue">Trade-in Value ($)</Label>
                      <Input
                        type="number"
                        name="tradeInValue"
                        id="tradeInValue"
                        value={formData.tradeInValue}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="0.00"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="taxRate">Tax Rate (%)</Label>
                      <Input
                        type="number"
                        name="taxRate"
                        id="taxRate"
                        value={formData.taxRate}
                        onChange={handleInputChange}
                        className="form-control"
                        step="0.01"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label for="termMonths">Term (Months)</Label>
                      <Input
                        type="select"
                        name="termMonths"
                        id="termMonths"
                        value={formData.termMonths}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        <option value="12">12 Months</option>
                        <option value="24">24 Months</option>
                        <option value="36">36 Months</option>
                        <option value="48">48 Months</option>
                        <option value="60">60 Months</option>
                        <option value="72">72 Months</option>
                        <option value="84">84 Months</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="interestRate">Interest Rate (%)</Label>
                      <Input
                        type="number"
                        name="interestRate"
                        id="interestRate"
                        value={formData.interestRate}
                        onChange={handleInputChange}
                        className="form-control"
                        step="0.01"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="12">
                    <Button
                      style={{ ...activeColorStyle, color: 'white' }}
                      onClick={calculateDeal}
                      block
                    >
                      Calculate Deal
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
        <Col md="6">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Deal Summary</CardTitle>
            </CardHeader>
            <CardBody>
              {calculatedResults ? (
                <Table responsive>
                  <tbody>
                    <tr>
                      <td><strong>Vehicle Price:</strong></td>
                      <td>${formData.vehiclePrice || "0.00"}</td>
                    </tr>
                    <tr>
                      <td><strong>Trade-in Value:</strong></td>
                      <td>-${formData.tradeInValue || "0.00"}</td>
                    </tr>
                    <tr>
                      <td><strong>Subtotal:</strong></td>
                      <td>${calculatedResults.subtotal}</td>
                    </tr>
                    <tr>
                      <td><strong>Tax ({formData.taxRate}%):</strong></td>
                      <td>${calculatedResults.taxAmount}</td>
                    </tr>
                    <tr>
                      <td><strong>Total Amount:</strong></td>
                      <td><strong>${calculatedResults.totalAmount}</strong></td>
                    </tr>
                    <tr>
                      <td><strong>Down Payment:</strong></td>
                      <td>-${formData.downPayment || "0.00"}</td>
                    </tr>
                    <tr>
                      <td><strong>Financed Amount:</strong></td>
                      <td><strong>${calculatedResults.financedAmount}</strong></td>
                    </tr>
                    <tr>
                      <td><strong>Monthly Payment:</strong></td>
                      <td><strong>${calculatedResults.monthlyPayment}</strong></td>
                    </tr>
                    <tr>
                      <td><strong>Total Interest:</strong></td>
                      <td>${calculatedResults.totalInterest}</td>
                    </tr>
                    <tr>
                      <td><strong>Total Cost:</strong></td>
                      <td><strong>${calculatedResults.totalCost}</strong></td>
                    </tr>
                  </tbody>
                </Table>
              ) : (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  minHeight: '200px',
                  color: '#6c757d',
                  fontSize: '16px'
                }}>
                  Enter vehicle details and click "Calculate Deal" to see the summary
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default FigureADeal; 